// @ts-strict-ignore
import * as React from 'react';
import {PureComponent} from 'react';
import {clsx} from 'clsx';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import ResizeObserver from 'resize-observer-polyfill';
import debounce from 'lodash/debounce';

import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {SpinnerReact} from 'common/components/spinner';
import {SegmentLegendHeader} from 'common/charts/misc/segment-legend-header';
import {CountType} from 'common/report/funnels/models/types';
import {
  BarSelection,
  ConversionColumnViz,
  OnStepBarClickArgs,
  OnStepHorizontalWheelArgs,
} from 'common/charts/visualization/conversion-column-viz';
import {REPORT_LIMITS, ReportName} from 'common/report/constants';
import {EmptyDataReact} from 'common/charts/misc/empty-data';
import {
  ConversionStepBar,
  getConversionStepLayouts,
  NormalConversionStep,
  validateConversionSteps,
} from 'common/charts/visualization/conversion-column-viz/util';
import {ChartTooltipProps} from 'common/components/chart-tooltip';
import {TooltipBulletStyle, TooltipItemType, TooltipRow} from 'common/components/chart-tooltip/types';
import {formatMetricNumberWithUnits} from 'common/util';
import {CurrentProject} from 'common/report/model-resources/projects';
import {OVERALL_PROP} from 'common/report/funnels/models/constants';
import {calculatePopoverPosition} from 'common/report/insights/util/positioning';
import type {PopoverParams, StepPopoverAction, StepPopoverActionParams} from 'common/report/insights/util/chart';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {isSessionRecordingEnabled} from 'common/report/util/feature-sampling';
import {BehaviorShowClause, DisplayOptions} from 'common/report/insights/util/types';
import {ChartType, InsightsBookmarkParams, SegmentId} from 'common/types/reports/bookmark';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useSegmentFilterController} from 'common/contexts/react/segment-filter-controller-context';
import {sessionRecordingQueryUtils} from 'common/widgets/recording-player/query-utils';
import {IronContextRegistry} from 'common/report/context-registry';
import {useTranslation} from 'common/i18n/hooks';
import {eventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {getFinalDisplayOptions} from 'common/report/insights/charts/util';
import {PropertyDisplayOptions} from 'apps/lexicon/src/models/property-definition';
import {isFunnelConversionShowClause} from 'common/report/insights/models/utils';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {getViewReplaysDisabledMessage} from 'iron/common/report/insights/visualization/charts/chart-display/utils';

import {ChartPopover} from './popover';
import css from './index.module.styl?inline';
import {
  FunnelsStepProjectDetails,
  getBreakdownSegmentProp,
  getComparisonBarIndex,
  getLegendItemsWithDataTableRows,
  getMainBarIndex,
  OVERALL_CONTROL_GROUP,
  StepChartData,
} from './util';
import {BarType, FunnelStepSegmentSelection, MultimetricData} from './types';
import {MultimetricStepBookmarkController} from './bookmark-controller/multimetric';
import {StepsChartPopover} from './mixins';
import styles from './index.module.styl';

export type OnTrackData = {
  eventName: string;
  properties: Obj;
};

export type OnChangeControlGroupData = {props: SegmentId[]};

type FunnelsStepChartState = {
  popoverOpen: boolean;
  popoverParams: PopoverParams;
  selectedSegment: FunnelStepSegmentSelection | null;
};

type FunnelsStepChartProps = {
  bookmarkData?: MultimetricData;
  enableSegmentClick?: boolean;
  failed?: boolean;
  loaded?: boolean;
  minStepWidth?: number;
  maxStepWidth?: number;
  onChangeControlGroup?: (data: OnChangeControlGroupData) => void;
  onTrack?: (data: OnTrackData) => void;
  popoverAction?: (params: StepPopoverActionParams) => void;
  /**
   * project details like projectAsOfDate are typically attributes instead of
   * an object prop, but projectAsOfDate supports null/empty as a valid value
   * which makes it impractical for a 'project-as-of-date' attribute to
   * distinguish between "set to null/empty" and "not set, default to Store"
   */
  projectDetails?: FunnelsStepProjectDetails | null;
  resizingLayout?: boolean;
  segmentFilterController?: SegmentFilterController;
};

export interface FunnelsStepChartJsxProps {
  props: FunnelsStepChartProps;
}

class FunnelsStepChart extends PureComponent<
  FunnelsStepChartProps & Pick<IronContextRegistry, `currentProject` | `i18n` | `metrics`>,
  FunnelsStepChartState
> {
  private declare _bookmarkController: MultimetricStepBookmarkController;
  private ref: React.RefObject<HTMLDivElement> = React.createRef();

  state = {
    popoverOpen: false,
    popoverParams: null,
    selectedSegment: null,
  };

  get bookmarkController(): MultimetricStepBookmarkController {
    const data = this.props.bookmarkData;
    if (!data) {
      return null;
    }

    const dataDisplay = ((data.bookmark as InsightsBookmarkParams)?.sections?.show || []).map(
      (clause) => clause.display,
    );
    const bcDisplay = (this._bookmarkController?.showClauses || []).map((clause) => clause.display);
    // Create bookmark controller on first initialization or our data type has changed
    if (!this._bookmarkController || !isEqual(dataDisplay, bcDisplay)) {
      this._bookmarkController = new MultimetricStepBookmarkController(data.bookmark, data.queryResults);
    }
    return this._bookmarkController;
  }

  private declare selfResizeObserver?: ResizeObserver;
  private selfWidth = 0;

  componentDidMount() {
    const debouncedUpdateLegend = debounce(this.updateLegend.bind(this), 100, {leading: true});
    this.selfResizeObserver = new ResizeObserver((entries) => {
      this.selfWidth = entries[0].contentRect.width;
      debouncedUpdateLegend(this.getStepChartData());
    });
    this.selfResizeObserver.observe(this.ref.current);
  }

  componentDidUpdate(prevProps: Readonly<FunnelsStepChartProps>): void {
    if (!isEqual(prevProps.bookmarkData, this.props.bookmarkData)) {
      // Fixes a bug where sorting was not being applied.
      // This calls `segmentFilterController.loadSections`, which applies the new sortConfig
      this._bookmarkController = null;
      this.updateLegend(this.getStepChartData());
    }
  }

  componentWillUnmount() {
    this.selfResizeObserver.disconnect();
  }

  private getStepChartData(): StepChartData {
    if (this.getProjectDetails()) {
      return this.bookmarkController?.getStepChartData();
    }
    return null;
  }

  private getCurrentProject(): CurrentProject {
    return this.props.currentProject.get().value;
  }

  getActiveDataGroup() {
    const activeDataGroupId = this.bookmarkController?.getDataGroupId();

    if (activeDataGroupId && dataGroupStore.value) {
      return dataGroupStore.value[activeDataGroupId];
    }
    return null;
  }

  get bookmarkDisplayOptions(): Partial<DisplayOptions> {
    if (`displayOptions` in this.props.bookmarkData.bookmark) {
      return this.props.bookmarkData.bookmark.displayOptions;
    }
    return {};
  }

  private getPopoverParams(
    stepChartData: StepChartData,
    {barIndex, color, isComparison, isDropoff, originalMouseEvent, stepIndex}: OnStepBarClickArgs,
    visibleConversionStep: NormalConversionStep<never, string>,
    segmentProp: string,
  ): PopoverParams {
    const {
      comparisonDateRange,
      comparisonDateRangeLabel,
      countType,
      funnelSegmentsForPopover,
      funnelSegments,
      funnelCompareSegments,
    } = stepChartData;

    const selectedBar = visibleConversionStep.bars[barIndex];
    const bar = {
      type: isDropoff ? BarType.DROP_OFF : BarType.CONVERTED,
      isComparison,
      comparisonDateRange,
      color,
      index: stepIndex,
      barIdx: barIndex,
    };
    this.setState({
      selectedSegment: {
        stepIndex,
        disableTooltips: true,
        isDropoff,
        segmentProp,
      },
    });

    const dataGroup = this.getActiveDataGroup();

    const toDate = (this.bookmarkController as MultimetricStepBookmarkController).queryResults.date_range.to_date;
    const toDateMs = new Date(toDate).getTime();
    const showClause = this.bookmarkController.getFunnelShowClause();

    const replaysDisabledReason = sessionRecordingQueryUtils.getReportReplaysDisabledReason({
      replayTimestampMs: toDateMs,
      currentProject: this.getCurrentProject(),
      showClauses: [showClause],
    });

    const {print, t} = this.props.i18n;
    const options = {
      canViewInFlows: true,
      canViewReplays: isSessionRecordingEnabled(this.getCurrentProject()),
      dataGroup,
      dataGroupName: dataGroup?.displayName ?? `Users`,
      disabledViewInFlowsButtonMessage: print(this.bookmarkController.getDisableViewInFlowsButtonMessage()),
      disableViewUsersButtonMessage: print(this.bookmarkController.getDisableViewUsersButtonMessage()),
      disabledViewReplaysButtonMessage: getViewReplaysDisabledMessage(replaysDisabledReason, t),
    };

    const popover = new ChartPopover(
      funnelSegmentsForPopover,
      funnelCompareSegments,
      funnelSegments,
      bar,
      this.props.i18n,
      (value, isPercent, defaultOptions, overrideOptions) =>
        this.formatter(showClause, value, isPercent, defaultOptions, overrideOptions),
      options,
      !!comparisonDateRangeLabel,
    );
    const popoverStyles = calculatePopoverPosition({
      barElement: originalMouseEvent.target as HTMLElement,
      viewerElement: this.ref.current,
    });
    return {
      open: true,
      bar,
      // cohort: cohortDetails[0].cohort, TODO cohorts
      // cohortDetails,
      groupByValue: stepChartData.conversionSteps[bar.index].displayName,
      segmentProp,
      styles: popoverStyles,
      isSegmented: popover.isSegmented(),
      isDropOff: popover.isDropOff(),
      title: selectedBar.displayName,
      buttonOptions: popover.getButtonOptions(),
      slottedContent: popover.getSlottedContent(countType),
    };
  }

  private isCountBasedMetric(stepChartData: StepChartData) {
    // Count type is handled differently for multimetric and funnels, TODO clean up terminology
    return stepChartData.countType !== CountType.ConversionRate;
  }

  private renderTemplate() {
    return (
      <div className={clsx(styles.root, {[styles.resizingLayout]: this.props.resizingLayout})} ref={this.ref}>
        <style>{css}</style>
        {this.renderInnerTemplate()}
      </div>
    );
  }

  private formatter(
    showClause: BehaviorShowClause,
    value: number,
    isPercent: boolean,
    defaultOptions: Partial<PropertyDisplayOptions> = {},
    overrideOptions: Partial<PropertyDisplayOptions> = {},
  ): string {
    const isPercentageView = isFunnelConversionShowClause(showClause);
    if (isPercent) {
      value *= 100;
      defaultOptions = {suffix: `%`};
    }
    let options = {...defaultOptions, ...overrideOptions};
    // We only want to format the value if it matches the math type of the show
    // clause. For example if the math type is "Conversion Rate" we only want to
    // format the percent value (the actual conversion rate) and not the absolute
    // value and vice versa if our math type is Unique Users or something else.
    if (isPercentageView === isPercent) {
      options = getFinalDisplayOptions(
        showClause,
        eventPropertyDefinitionsStore,
        this.props.metrics,
        defaultOptions,
        overrideOptions,
      );
    }

    return formatMetricNumberWithUnits(value, options);
  }

  private renderInnerTemplate() {
    const loaded = this.props.loaded && this.hasRequiredParams();

    if (!loaded) {
      return <SpinnerReact className={styles.mpSpinner} elref={`loadingSpinner`} />;
    }

    if (this.bookmarkController.isEmptyReport()) {
      return <EmptyDataReact />;
    }

    // Compute this on the fly to reduce statefulness. If it becomes slow we can memoize it.
    const stepChartData = this.getStepChartData();

    const primaryYAxisOptions = this.bookmarkDisplayOptions.primaryYAxisOptions;
    const secondaryYAxisOptions = this.bookmarkDisplayOptions.secondaryYAxisOptions;
    const visibleConversionSteps = this.getVisibleConversionSteps();
    validateConversionSteps(visibleConversionSteps); // will throw errors if conversion steps are invalid
    // Just compute this on the fly. If it becomes slow we can memoize it.
    const stepLayouts = getConversionStepLayouts(
      visibleConversionSteps,
      this.isCountBasedMetric(stepChartData),
      primaryYAxisOptions,
    ); // cannot be in state because it must always be sync with `this.conversionSteps`

    const secondaryStepLayouts = getConversionStepLayouts(
      visibleConversionSteps,
      this.isCountBasedMetric(stepChartData),
      secondaryYAxisOptions,
    );

    const showClause = this.bookmarkController.getFunnelShowClause();
    return (
      <>
        {this.state.popoverOpen && (
          <StepsChartPopover
            popoverParams={{
              open: true,
              ...this.state.popoverParams,
              onAction: (ev) =>
                this.props.popoverAction?.({
                  action: ev.detail.action as StepPopoverAction,
                  stepIndex: this.state.selectedSegment?.stepIndex,
                  segmentProp: this.state.selectedSegment?.segmentProp,
                  isDropOff: this.state.selectedSegment?.isDropoff,
                  color: this.state.popoverParams.bar.color,
                  metricLabel: stepChartData.funnelMetricLabel,
                  isComparison: this.state.popoverParams.bar.isComparison,
                  comparisonDateRange: this.state.popoverParams.bar.comparisonDateRange,
                  barType: this.state.popoverParams.bar.type,
                }),
              onClose: () => this.setState({popoverOpen: false, selectedSegment: null}),
            }}
          />
        )}
        <SegmentLegendHeader
          className={styles.legendHeader}
          segmentFilterController={this.props.segmentFilterController}
        />
        <ConversionColumnViz
          className={styles.stepChart}
          {...{
            ...(this.props.minStepWidth ? {minStepWidth: this.props.minStepWidth} : {}),
            ...(this.props.maxStepWidth ? {maxStepWidth: this.props.maxStepWidth} : {}),
            conversionSteps: visibleConversionSteps,
            enableBarClick: this.props.enableSegmentClick,
            formatter: (value, isPercent, defaultOptions, overrideOptions) =>
              this.formatter(showClause, value, isPercent, defaultOptions, overrideOptions),
            onStepBarClick: (args) => this.handleStepBarClick(stepChartData, args),
            onStepHorizontalWheel: (args) => this.handleStepHorizontalWheel(args),
            primaryYAxisOptions,
            secondaryStepLayouts,
            secondaryYAxisOptions,
            selectedBars: this.getSelectedStepBars(stepChartData),
            stepBarTooltipCallback: this.getTooltipData.bind(this, showClause, stepChartData),
            stepLayouts,
            useAbsoluteValues: this.isCountBasedMetric(stepChartData),
            segmentFilterController: this.props.segmentFilterController,
          }}
        />
      </>
    );
  }

  private getProjectDetails(): null | FunnelsStepProjectDetails {
    if (this.props.projectDetails) {
      return this.props.projectDetails;
    }
    const currentProject = this.getCurrentProject();
    if (currentProject?.id) {
      return {
        projectId: currentProject.id.toString(),
        projectAsOfDate: currentProject.projectAsOfDate,
        projectTimeZone: currentProject.timezone,
      };
    }
    return null;
  }

  private hasRequiredParams(): boolean {
    return Boolean(this.bookmarkController && this.getProjectDetails());
  }

  private getSelectedStepBars(stepChartData: StepChartData): BarSelection[] {
    const selectedSegment = this.state.selectedSegment;
    const selections: BarSelection[] = [];
    if (selectedSegment && stepChartData) {
      const conversionStep = this.getVisibleConversionSteps()[selectedSegment.stepIndex];
      selections.push({
        stepIndex: selectedSegment.stepIndex,
        barIndex: getMainBarIndex(conversionStep, selectedSegment.segmentProp),
        isDropoff: selectedSegment.isDropoff,
      });

      const hasComparisonData = !!stepChartData.comparisonDateRangeLabel;
      if (hasComparisonData) {
        selections.push({
          stepIndex: selectedSegment.stepIndex,
          barIndex: getComparisonBarIndex(conversionStep, selectedSegment.segmentProp),
          isDropoff: selectedSegment.isDropoff,
        });
      }
    }
    return selections;
  }

  private getVisibleConversionSteps(): Array<NormalConversionStep<never, string>> {
    const stepChartData = this.bookmarkController.getStepChartData();
    // TODO this does nothing specific to the bookmark type, move it back to the abstract file once multimetric is more mature
    const result =
      stepChartData?.conversionSteps?.map((step): NormalConversionStep<never, string> => {
        const barLookup = new Map<string, ConversionStepBar[]>();
        step.bars.forEach((bar) => {
          const segmentKey = bar.extras;
          if (segmentKey || !isNil(bar.displayName)) {
            if (!barLookup.has(segmentKey)) {
              barLookup.set(segmentKey, []);
            }
            barLookup.get(segmentKey).push(bar);
          }
        });

        const selectedItem = this.props.segmentFilterController.getSelectedItems(0);
        const filteredSelectedItem = selectedItem.filter((item) => barLookup.has(item.id));
        const subResult = {
          ...step,
          bars: filteredSelectedItem.flatMap((item) => {
            const bars = barLookup.get(item.id);
            return bars.map((bar) => ({
              ...bar,
              color: this.props.segmentFilterController.getItemColor(0, bar.extras),
            }));
          }),
        };
        return subResult;
      }) ?? [];
    return result;
  }

  private updateLegend(stepChartData: StepChartData): void {
    if (stepChartData) {
      const {rows, comparisonRows, columnGroups} = this.bookmarkController.getDataTableInfo({
        metricsResource: this.props.metrics,
        onStatSigClick: (_, row) => {
          if (row.props[0].prop === OVERALL_PROP) {
            this.props.onChangeControlGroup?.({props: OVERALL_CONTROL_GROUP});
          } else {
            this.props.onChangeControlGroup?.({props: row.props});
          }
        },
        stepChartData,
        tableWidth: this.selfWidth,
        i18n: this.props.i18n,
      });

      const legendSegmentItems = stepChartData.detailedLegendSegmentItems;
      this.props.segmentFilterController.loadSections(
        [
          {
            items: getLegendItemsWithDataTableRows({
              rows,
              comparisonRows,
              legendSegmentItems,
            }),
            label: stepChartData.legendSegmentLabel,
            tableColumns: columnGroups,
          },
        ],
        {
          tableSortConfig: this.bookmarkController.getSorting(),
          defaultNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelSteps].default,
          maxNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelSteps].max,
        },
      );
      this.props.segmentFilterController.loadFromSavedLegend(stepChartData.savedLegend);
      this.props.segmentFilterController.segmentationQueryLimit = REPORT_LIMITS[ReportName.Funnels].query;
      this.props.segmentFilterController.exportSegmentationQueryLimit = REPORT_LIMITS[ReportName.Funnels].csvExport;

      this.forceUpdate();
    }
  }

  private getSingleBarTooltipRows(params: {
    barIndex: number;
    conversionStep: NormalConversionStep<never, string>;
    formatter: (value: number, isPercent: boolean) => string;
    hoveredOnDropoff: boolean;
    previousConversionStep: null | NormalConversionStep<never, string>;
    stepChartData: StepChartData;
  }): TooltipRow[] {
    const {barIndex, conversionStep, formatter, hoveredOnDropoff, previousConversionStep, stepChartData} = params;

    const bar = conversionStep.bars[barIndex];

    let percent = 0,
      count = 0,
      dropoffValue = 0;
    if (bar) {
      percent = hoveredOnDropoff ? 1 - bar.conversionRate : bar.conversionRate;
      dropoffValue = previousConversionStep
        ? previousConversionStep.bars[barIndex].value - conversionStep.bars[barIndex].value
        : 0;
      count = hoveredOnDropoff ? dropoffValue : conversionStep.bars[barIndex].value;
    }

    const {t} = this.props.i18n;
    const percentDescription = hoveredOnDropoff
      ? t(`chart-actions.tooltip.dropped-off-users`)
      : t(`chart-actions.tooltip.converted-users`);
    let countDescription;
    switch (stepChartData.countType) {
      case CountType.PropertySum:
        countDescription = this.bookmarkController.getPropSumLabel();
        break;
      case CountType.Unique:
        countDescription = t(`chart-actions.tooltip.uniques`);
        break;
      case CountType.Session:
        countDescription = t(`chart-actions.tooltip.sessions`);
        break;
      case CountType.General:
      default:
        countDescription = t(`chart-actions.tooltip.totals`);
        break;
    }

    return [
      {
        items: [
          {text: formatter(percent, true), type: TooltipItemType.Data},
          {text: percentDescription, type: TooltipItemType.Text},
        ],
      },
      {
        items: [
          {text: formatter(count, false), type: TooltipItemType.Data},
          {text: countDescription, type: TooltipItemType.Text},
        ],
      },
    ];
  }

  private getTooltipData(
    showClause: BehaviorShowClause,
    stepChartData: StepChartData,
    conversionStep: NormalConversionStep<never, string>,
    previousConversionStep: null | NormalConversionStep<never, string>,
    barIndex: number,
    hoveredOnDropoff: boolean,
  ): ChartTooltipProps {
    if (this.state.selectedSegment?.disableTooltips) {
      return {sections: []};
    }

    const bar = conversionStep.bars[barIndex];

    const formatter = (value: number, isPercent: boolean): string => {
      return this.formatter(
        showClause,
        value,
        isPercent,
        {},
        // Tooltip values should never be abbreviated.
        {abbrev: false},
      );
    };

    const {comparisonDateRangeLabel, mainDateRangeLabel} = stepChartData;
    const hasComparisonData = !!comparisonDateRangeLabel;
    if (hasComparisonData) {
      const breakdownSegmentProp = getBreakdownSegmentProp(conversionStep, barIndex);
      const mainBarIndex: number = getMainBarIndex(conversionStep, breakdownSegmentProp);
      const comparisonBarIndex: number = getComparisonBarIndex(conversionStep, breakdownSegmentProp);

      const mainDataRows = this.getSingleBarTooltipRows({
        barIndex: mainBarIndex,
        conversionStep,
        formatter,
        hoveredOnDropoff,
        previousConversionStep,
        stepChartData,
      });
      const comparisonDataRows = this.getSingleBarTooltipRows({
        barIndex: comparisonBarIndex,
        conversionStep,
        formatter,
        hoveredOnDropoff,
        previousConversionStep,
        stepChartData,
      });
      const bulletColor = conversionStep.bars[mainBarIndex].color;
      return {
        sections: [
          {
            rows: [{items: [{text: bar.displayName, type: TooltipItemType.Strong}]}],
          },
          {
            bulletColor,
            bulletStyle: TooltipBulletStyle.Solid,
            rows: [{items: [{text: mainDateRangeLabel, type: TooltipItemType.Strong}]}, ...mainDataRows],
          },
          {
            bulletColor,
            bulletStyle: TooltipBulletStyle.Dash,
            rows: [{items: [{text: comparisonDateRangeLabel, type: TooltipItemType.Strong}]}, ...comparisonDataRows],
          },
        ],
      };
    }

    const dataRows = this.getSingleBarTooltipRows({
      barIndex,
      conversionStep,
      formatter,
      hoveredOnDropoff,
      previousConversionStep,
      stepChartData,
    });
    return {
      sections: [
        {
          rows: [{items: [{text: bar.displayName, type: TooltipItemType.Strong}]}],
        },
        {
          rows: dataRows,
        },
      ],
      titleColor: bar.color,
    };
  }

  private handleStepBarClick(stepChartData: StepChartData, args: OnStepBarClickArgs): void {
    const conversionStep = this.getVisibleConversionSteps()[args.stepIndex];
    const breakdownSegmentProp = getBreakdownSegmentProp(conversionStep, args.barIndex);
    this.setState({
      popoverOpen: true,
      popoverParams: this.getPopoverParams(stepChartData, args, conversionStep, breakdownSegmentProp),
    });
  }

  private handleStepHorizontalWheel(args: OnStepHorizontalWheelArgs): void {
    this.props.onTrack?.({
      eventName: `[Funnels] Scroll chart steps`,
      properties: {
        startIndex: args.firstVisibleStepIndex,
        direction: args.direction,
      },
    });
  }

  render() {
    return this.renderTemplate();
  }
}

export const FunnelsStepChartReact = (props: FunnelsStepChartProps) => {
  const currentProject = useCurrentProject();
  const segmentFilterController = useSegmentFilterController() || new SegmentFilterController();
  const i18n = useTranslation();
  const metrics = useMetrics();
  if (currentProject?.id && !currentProject.get()?.value) {
    return <div />;
  }
  return (
    <FunnelsStepChart
      {...props}
      segmentFilterController={segmentFilterController}
      currentProject={currentProject}
      i18n={i18n}
      metrics={metrics}
    />
  );
};
